import { useState, useEffect, useCallback } from "react";

type DeviceInfo = {
  readonly deviceId: string;
  readonly label: string;
};

type DeviceEnumeration = {
  readonly cameras: readonly DeviceInfo[];
  readonly microphones: readonly DeviceInfo[];
  readonly refresh: () => Promise<void>;
};

export const requestMediaPermissions = async (): Promise<boolean> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: true,
    });
    for (const track of stream.getTracks()) {
      track.stop();
    }
    return true;
  } catch (error) {
    return false;
  }
};

export const useDeviceEnumeration = (): DeviceEnumeration => {
  const [cameras, setCameras] = useState<readonly DeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<readonly DeviceInfo[]>([]);

  const enumerate = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const videoInputs = devices
        .filter((d) => d.kind === "videoinput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Camera ${d.deviceId.slice(0, 4)}`,
        }));

      const audioInputs = devices
        .filter((d) => d.kind === "audioinput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${d.deviceId.slice(0, 4)}`,
        }));

      setCameras(videoInputs);
      setMicrophones(audioInputs);
    } catch (error) {
      setCameras([]);
      setMicrophones([]);
    }
  }, []);

  useEffect(() => {
    enumerate();

    const handler = () => {
      enumerate();
    };
    navigator.mediaDevices.addEventListener("devicechange", handler);
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", handler);
    };
  }, [enumerate]);

  return { cameras, microphones, refresh: enumerate };
};
